/**
 * Thin fetch wrapper for the JSON API. Always sends the session cookie (`credentials: "include"`),
 * serialises JSON bodies, and throws `ApiError` (with the parsed `error` code + details) on non-2xx
 * so React Query surfaces typed failures.
 */
import { API_BASE_URL } from "../lib/config";

export class ApiError extends Error {
  constructor(
    public readonly status: number,
    public readonly code: string,
    message?: string,
    public readonly details?: unknown,
  ) {
    super(message ?? code);
    this.name = "ApiError";
  }
}

export async function apiFetch<T>(
  path: string,
  options: { method?: string; body?: unknown } = {},
): Promise<T> {
  const hasBody = options.body !== undefined;
  const res = await fetch(`${API_BASE_URL}${path}`, {
    method: options.method ?? "GET",
    credentials: "include",
    headers: hasBody ? { "content-type": "application/json" } : undefined,
    body: hasBody ? JSON.stringify(options.body) : undefined,
  });
  const text = await res.text();
  const data = text ? JSON.parse(text) : null;
  if (!res.ok) {
    const code = (data && (data.error || data.code)) || "error";
    throw new ApiError(res.status, code, data?.message, data?.details);
  }
  return data as T;
}
